import React, { useState, useEffect, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import { api } from "../api/client";
import { getStandardComments } from "../constants/standardComments";

// Works for findings (has finding.id), presets (has preset.id as assessmentId),
// or freeform issues (zone + category, e.g. from dip reviews)
function CreateIssueForm({ finding, preset, siteId, storeId, storeName, zone, category, defaultSeverity, imageUrl, onCreated, onCancel, currentUser, fixtureType, scanHour }) {
    const { accounts } = useMsal();
    const account = accounts[0];

    // Gap-analysis pre-fill, when the scan produced one. Absent on every other
    // path (dip reviews, freeform, non-AI fixtures), and the form then behaves
    // exactly as it did before auto-fill existed.
    const suggestion = preset?.suggestion || null;

    const [routingOptions, setRoutingOptions] = useState(null);
    const [assignTo, setAssignTo] = useState(suggestion?.assignTo || "Site");
    const [comment, setComment] = useState("");
    const [title, setTitle] = useState(suggestion?.title || "");
    const [cc, setCc] = useState("");
    const [sendToSelf, setSendToSelf] = useState(false);
    // Default OFF: RPLs now receive a daily digest of opened issues instead of a
    // per-ticket CC. Reviewers can still tick this to loop in the RPL immediately.
    // A suggestion overrides that default when the severity escalates to the
    // Area Manager, per the Severity & Escalation deck.
    const [ccRpl, setCcRpl] = useState(false);
    // "Severity is a mandatory field - no pre-filled default. Reviewers must
    // actively assign the correct tag before an issue can be submitted."
    //   -- Severity & Escalation deck
    //
    // On a pre-filled form this starts genuinely empty rather than showing a
    // suggested tag: the scan supplies the counts, the reviewer supplies the
    // judgement. Every other caller keeps the default it always had.
    const [severity, setSeverity] = useState(
        suggestion ? "" : (defaultSeverity || finding?.severity || "warning")
    );
    const [includePhoto, setIncludePhoto] = useState(true);
    const [smsManager, setSmsManager] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const isPresetLevel = !finding && !!preset;
    const isFreeform = !finding && !preset;

    // Standard comments are restricted to the fixture this issue is being
    // raised against — a coffee station offers the coffee comments, etc.
    const effectiveFixtureType = fixtureType || preset?.fixtureType || finding?.fixtureType;
    const commentGroups = useMemo(() => getStandardComments(effectiveFixtureType), [effectiveFixtureType]);

    // Appends rather than replaces, so a reviewer can stack a preset comment on
    // top of whatever they have already typed.
    const applyStandardComment = (text) => {
        if (!text) return;
        setComment(prev => (prev.trim() ? `${prev.replace(/\s+$/, "")}\n${text}` : text));
    };

    // Seed the comment from the standard-comment pack plus what the scan
    // actually saw. The standard line is verbatim from Simon Gillies' deck and
    // is what the site manager is used to reading; the AI detail underneath is
    // the specifics (how many trays, which products, where). Runs once on
    // mount so it never fights the reviewer's own typing.
    useEffect(() => {
        if (!suggestion) return;
        const standard = commentGroups[0]?.comments?.[0] || "";
        const seeded = [standard, suggestion.detail].filter(Boolean).join("\n");
        if (seeded) setComment(seeded);
        // Mount-only by design: re-seeding on any dep change would overwrite
        // whatever the reviewer has typed.
    }, []);

    // Severity drives escalation, so changing it has to re-apply the routing
    // that goes with it -- otherwise a reviewer who downgrades CRITICAL to LOW
    // still silently SMSes the site manager. Uses the policy table shipped
    // with the suggestion (src/api/issueSuggestion.js) so the deck's matrix
    // lives in exactly one place.
    const handleSeverityChange = (value) => {
        setSeverity(value);
        const routing = suggestion?.policy?.routing?.[value];
        if (routing) {
            setAssignTo(routing.assignTo);
            setCcRpl(routing.ccRpl || suggestion.isRepeat);
            setSmsManager(routing.smsManager);
        }
    };

    // Shown only once the reviewer has chosen a tag -- it describes what they
    // just committed the site to, not what the scan thinks.
    const suggestedExpectation = suggestion?.policy?.responseExpectation?.[severity];

    useEffect(() => {
        api.routingOptions(siteId)
            .then(data => setRoutingOptions(data))
            .catch(err => setError("Failed to load routing options: " + err.message));
    }, [siteId]);

    const selectedTeam = routingOptions?.teams?.find(t => t.team === assignTo);
    // Hide CC RPL when assigning directly to RPL
    const showCcRpl = assignTo !== 'RPL' && routingOptions?.rplEmail;
    const emailWillDefer = assignTo === "Site" && !!scanHour;

    const handleSubmit = async () => {
        if ((isPresetLevel || isFreeform) && !title.trim() && !comment.trim()) {
            setError("Please enter a title or comment describing the issue");
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            let result;
            const reviewerEmail = account?.username || currentUser?.username || "web_user";
            const shared = {
                assignTo,
                comment,
                severity,
                siteId,
                includePhoto,
                smsManager,
                cc: cc.trim() || undefined,
                sendToSelf: sendToSelf ? reviewerEmail : undefined,
                ccRpl: showCcRpl ? ccRpl : false,
                reviewerEmail,
                reviewerName: account?.name || currentUser?.name || account?.username || "Web User",
                scanHour: scanHour || undefined,
            };
            if (isFreeform) {
                result = await api.createIssueFromPreset({
                    assessmentId: null,
                    title: title.trim() || `${zone || "dip"} — issue reported`,
                    zone: zone || "dip_form",
                    category: category || "fuel_compliance",
                    imageUrl: includePhoto ? imageUrl : undefined,
                    storeId,
                    ...shared,
                });
            } else if (isPresetLevel) {
                result = await api.createIssueFromPreset({
                    assessmentId: preset.id,
                    title: title.trim() || `${preset.checkType || preset.presetName} — issue reported`,
                    ...shared,
                });
            } else {
                result = await api.createIssueFromFinding({
                    findingIds: [finding.id],
                    ...shared,
                });
            }
            onCreated(result);
        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div style={{
            background: "var(--bg-primary)", border: "1px solid var(--primary)",
            borderRadius: 8, padding: 16, marginTop: 8
        }}>
            <div style={{ fontWeight: 600, marginBottom: 12, color: "var(--text-primary)" }}>
                {isFreeform ? "Create Issue from Finding" : isPresetLevel ? "Create Issue from Scan" : "Create Issue from Finding"}
            </div>

            {suggestion && (
                <div style={{
                    marginBottom: 12, padding: "8px 10px", borderRadius: 6,
                    background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.35)",
                    fontSize: 12, lineHeight: 1.5,
                }}>
                    <div style={{ fontWeight: 700, color: "#3b82f6", marginBottom: 2 }}>
                        Pre-filled from scan
                        {suggestion.isRepeat && (
                            <span style={{
                                marginLeft: 8, padding: "1px 7px", borderRadius: 8,
                                background: "rgba(139,92,246,0.15)", color: "#8b5cf6", fontSize: 10,
                            }}>
                                REPEAT &times;{suggestion.priorIssueCount}
                            </span>
                        )}
                    </div>
                    <div style={{ color: "var(--text-secondary)" }}>
                        Title and comment below are filled in from what the scan counted &mdash;
                        edit anything. Severity is not pre-filled: set it and the routing follows.
                    </div>
                    {/* Escalation the deck promises but the system cannot yet
                        deliver. Better to say so on the form than to let a
                        reviewer believe National Ops was notified. */}
                    {severity === "critical" && !suggestion.nationalOpsConfigured && (
                        <div style={{ color: "#f97316", marginTop: 4 }}>
                            CRITICAL should also reach National Ops &mdash; no routing address is
                            configured, so this will reach the site and the Area Manager only.
                        </div>
                    )}
                    {ccRpl && !suggestion.rplAvailable && (
                        <div style={{ color: "#f97316", marginTop: 4 }}>
                            No Area Manager (RPL) is mapped to this site, so the CC cannot be sent.
                        </div>
                    )}
                </div>
            )}

            <div style={{ display: "grid", gap: 10 }}>
                {(isPresetLevel || isFreeform) && (
                    <div>
                        <label style={{ display: "block", fontSize: 13, color: "var(--text-secondary)", marginBottom: 4 }}>Issue Title</label>
                        <input
                            type="text"
                            className="action-input"
                            placeholder={isFreeform ? "e.g. Dip procedure not followed correctly" : `e.g. ${preset?.checkType || preset?.presetName} needs attention`}
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            style={{ width: "100%", boxSizing: "border-box", padding: "6px 10px" }}
                        />
                    </div>
                )}

                <div>
                    <label style={{ display: "block", fontSize: 13, color: "var(--text-secondary)", marginBottom: 4 }}>Assign To</label>
                    <select
                        className="filter-select"
                        value={assignTo}
                        onChange={(e) => setAssignTo(e.target.value)}
                        style={{ width: "100%" }}
                    >
                        {(routingOptions?.teams || []).map(t => (
                            <option key={t.team} value={t.team} disabled={!t.email}>
                                {t.team} — {t.display}{!t.email ? " (no email)" : ""}
                            </option>
                        ))}
                    </select>
                    {selectedTeam && selectedTeam.email && (
                        <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>
                            Email: {selectedTeam.email}
                            {emailWillDefer && " — sent when this review is closed, not now"}
                        </div>
                    )}
                </div>

                <div>
                    <label style={{ display: "block", fontSize: 13, color: "var(--text-secondary)", marginBottom: 4 }}>
                        Severity
                        {suggestion && !severity && (
                            <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: "#f97316" }}>
                                YOUR CALL &mdash; NOT PRE-FILLED
                            </span>
                        )}
                    </label>
                    <select
                        className="filter-select"
                        value={severity}
                        onChange={(e) => handleSeverityChange(e.target.value)}
                        style={{
                            width: "100%",
                            border: suggestion && !severity ? "2px solid #f97316" : undefined,
                        }}
                    >
                        {suggestion && (
                            <option value="" disabled>
                                Set the severity&hellip;
                            </option>
                        )}
                        {["critical", "high", "warning", "medium", "low", "info"].map(s => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </select>
                    {suggestion && suggestedExpectation && (
                        <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>
                            Response expectation: {suggestedExpectation}
                        </div>
                    )}
                </div>

                <div>
                    <label style={{ display: "block", fontSize: 13, color: "var(--text-secondary)", marginBottom: 4 }}>Comment</label>
                    <select
                        className="filter-select"
                        value=""
                        onChange={(e) => { applyStandardComment(e.target.value); e.target.value = ""; }}
                        style={{ width: "100%", marginBottom: 6 }}
                    >
                        <option value="">Standard comment — pick from the list or type your own below</option>
                        {commentGroups.map(g => (
                            <optgroup key={g.label} label={g.label}>
                                {g.comments.map(c => (
                                    <option key={c} value={c}>{c}</option>
                                ))}
                            </optgroup>
                        ))}
                    </select>
                    <textarea
                        className="action-input"
                        placeholder="Add context for the recipient..."
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        rows={2}
                        style={{ width: "100%", boxSizing: "border-box" }}
                    />
                </div>

                <div>
                    <label style={{ display: "block", fontSize: 13, color: "var(--text-secondary)", marginBottom: 4 }}>CC (optional)</label>
                    <input
                        type="text"
                        className="action-input"
                        placeholder="email@example.com, another@example.com"
                        value={cc}
                        onChange={(e) => setCc(e.target.value)}
                        style={{ width: "100%", boxSizing: "border-box", padding: "6px 10px" }}
                    />
                </div>

                <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
                        <input
                            type="checkbox"
                            checked={includePhoto}
                            onChange={(e) => setIncludePhoto(e.target.checked)}
                        />
                        Include photo
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
                        <input
                            type="checkbox"
                            checked={sendToSelf}
                            onChange={(e) => setSendToSelf(e.target.checked)}
                        />
                        Send copy to myself
                    </label>
                    {showCcRpl && (
                        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
                            <input
                                type="checkbox"
                                checked={ccRpl}
                                onChange={(e) => setCcRpl(e.target.checked)}
                            />
                            CC RPL now
                            <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
                                ({routingOptions.rplName || routingOptions.rplEmail} — otherwise gets the daily digest)
                            </span>
                        </label>
                    )}
                    <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
                        <input
                            type="checkbox"
                            checked={smsManager}
                            onChange={(e) => setSmsManager(e.target.checked)}
                        />
                        SMS Site Manager
                    </label>
                </div>
                {smsManager && routingOptions?.mobileNumbers?.length > 0 && (
                    <div style={{ fontSize: 12, color: "var(--text-secondary)", padding: "4px 0" }}>
                        {routingOptions.mobileNumbers.map((m, i) => (
                            <span key={i} style={{ marginRight: 12 }}>{m.name}: {m.number}</span>
                        ))}
                    </div>
                )}
                {smsManager && (!routingOptions?.mobileNumbers || routingOptions.mobileNumbers.length === 0) && (
                    <div style={{ fontSize: 12, color: "var(--text-muted)", fontStyle: "italic" }}>
                        No mobile numbers configured for this site
                    </div>
                )}

                {error && <div style={{ color: "var(--red)", fontSize: 13 }}>{error}</div>}

                {!severity && (
                    <div style={{ fontSize: 12, color: "#f97316" }}>
                        Set the severity above before sending.
                    </div>
                )}

                <div style={{ display: "flex", gap: 8 }}>
                    <button
                        className="action-submit"
                        onClick={handleSubmit}
                        disabled={submitting || !selectedTeam?.email || !severity}
                        style={{ flex: 1, opacity: severity ? 1 : 0.55 }}
                    >
                        {submitting ? "Creating..." : emailWillDefer ? "Create Issue" : "Create Issue & Send Email"}
                    </button>
                    <button className="action-cancel" onClick={onCancel}>Cancel</button>
                </div>
                {emailWillDefer && (
                    <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 8 }}>
                        Single collated email will be sent upon Close Review.
                    </div>
                )}
            </div>
        </div>
    );
}

export function IssueCreatedBanner({ result }) {
    return (
        <div style={{
            background: "rgba(34,197,94,0.1)", border: "1px solid #22c55e",
            borderRadius: 8, padding: 12, marginTop: 8, fontSize: 13
        }}>
            <strong style={{ color: "#22c55e" }}>Issue created</strong>
            <span style={{ marginLeft: 8 }}>
                Assigned to {result.assignedTo}
                {result.emailDeferred ? " — Collated email awaits Close Review." : result.emailSent ? " — email sent" : result.assignedEmail ? " — email failed" : " — no email (not configured)"}
                {result.smsSent && " — SMS sent"}
            </span>
        </div>
    );
}

export default CreateIssueForm;
